/**
 * FSD Part 12 — Projects. C17: one section per FSD feature specification
 * (12.2.1 PRJ-F01 … 12.2.7 PRJ-F07) plus the part catalogue (12.1) for
 * screens that implement a catalogue requirement rather than a feature.
 *
 * Item ids are stable: favourites and recents (localStorage) key off them, so
 * an item may move between sections but its id never changes.
 */
import type { NavGroup } from '../types'
import {
  PROJECTS_C11_PERMISSIONS,
  PROJECTS_PERMISSIONS,
} from '@/modules/projects/permissions'

export const P12_PROJECTS_GROUPS: NavGroup[] = [
  {
    id: 's-12.2.1',
    kind: 'feature',
    fsd: '12.2.1',
    code: 'PRJ-F01',
    labelKey: 'nav.fsd.s12_2_1.short',
    fullTitleKey: 'nav.fsd.s12_2_1.full',
    items: [
      {
        id: 'projects',
        labelKey: 'projects.routeTitles.projects',
        to: '/projects',
        icon: 'building',
        permission: PROJECTS_PERMISSIONS.projectRead,
        keywordsKey: 'nav.items.projects.keywords',
        descriptionKey: 'nav.items.projects.description',
      },
    ],
  },
  {
    id: 's-12.2.2',
    kind: 'feature',
    fsd: '12.2.2',
    code: 'PRJ-F02',
    labelKey: 'nav.fsd.s12_2_2.short',
    fullTitleKey: 'nav.fsd.s12_2_2.full',
    items: [
      {
        id: 'project-variations',
        labelKey: 'projects.c11.routeTitles.variations',
        to: '/projects/variations',
        icon: 'sitemap',
        permission: PROJECTS_C11_PERMISSIONS.variationRead,
        keywordsKey: 'nav.items.projectVariations.keywords',
        descriptionKey: 'nav.items.projectVariations.description',
      },
    ],
  },
  {
    id: 's-12.2.3',
    kind: 'feature',
    fsd: '12.2.3',
    code: 'PRJ-F03',
    labelKey: 'nav.fsd.s12_2_3.short',
    fullTitleKey: 'nav.fsd.s12_2_3.full',
    items: [
      {
        id: 'project-resources',
        labelKey: 'projects.c11.routeTitles.resources',
        to: '/projects/resources',
        icon: 'users',
        permission: PROJECTS_C11_PERMISSIONS.resourceRead,
        keywordsKey: 'nav.items.projectResources.keywords',
        descriptionKey: 'nav.items.projectResources.description',
      },
      {
        id: 'project-allocations',
        labelKey: 'projects.c11.routeTitles.allocations',
        to: '/projects/allocations',
        icon: 'calendar',
        permission: PROJECTS_C11_PERMISSIONS.allocationRead,
        keywordsKey: 'nav.items.projectAllocations.keywords',
        descriptionKey: 'nav.items.projectAllocations.description',
      },
    ],
  },
  {
    id: 's-12.2.4',
    kind: 'feature',
    fsd: '12.2.4',
    code: 'PRJ-F04',
    labelKey: 'nav.fsd.s12_2_4.short',
    fullTitleKey: 'nav.fsd.s12_2_4.full',
    items: [
      // FSD text line 15333: timesheet costing accounts — the accounts a
      // costed timesheet posts to are configured on this screen.
      {
        id: 'projects-settings',
        labelKey: 'projects.routeTitles.settings',
        to: '/projects/settings',
        icon: 'settings',
        permission: PROJECTS_PERMISSIONS.settingsManage,
        keywordsKey: 'nav.items.projectsSettings.keywords',
        descriptionKey: 'nav.items.projectsSettings.description',
      },
    ],
  },
  {
    id: 's-12.2.6',
    kind: 'feature',
    fsd: '12.2.6',
    code: 'PRJ-F06',
    labelKey: 'nav.fsd.s12_2_6.short',
    fullTitleKey: 'nav.fsd.s12_2_6.full',
    items: [
      {
        id: 'project-wip-runs',
        labelKey: 'projects.c11.routeTitles.wipRuns',
        to: '/projects/wip-runs',
        icon: 'scale',
        permission: PROJECTS_C11_PERMISSIONS.wipRead,
        keywordsKey: 'nav.items.projectWipRuns.keywords',
        descriptionKey: 'nav.items.projectWipRuns.description',
      },
      {
        id: 'project-contract-accounts',
        labelKey: 'projects.c11.routeTitles.contractSettings',
        to: '/projects/contract-settings',
        icon: 'settings',
        permission: PROJECTS_PERMISSIONS.settingsManage,
        keywordsKey: 'nav.items.projectContractAccounts.keywords',
        descriptionKey: 'nav.items.projectContractAccounts.description',
      },
    ],
  },
  {
    id: 's-12.2.7',
    kind: 'feature',
    fsd: '12.2.7',
    code: 'PRJ-F07',
    labelKey: 'nav.fsd.s12_2_7.short',
    fullTitleKey: 'nav.fsd.s12_2_7.full',
    items: [
      {
        id: 'project-retentions',
        labelKey: 'projects.c11.routeTitles.retentions',
        to: '/projects/retentions',
        icon: 'clock',
        permission: PROJECTS_C11_PERMISSIONS.retentionRead,
        keywordsKey: 'nav.items.projectRetentions.keywords',
        descriptionKey: 'nav.items.projectRetentions.description',
      },
      {
        id: 'project-advances',
        labelKey: 'projects.c11.routeTitles.advances',
        to: '/projects/advances',
        icon: 'download',
        permission: PROJECTS_C11_PERMISSIONS.advanceManage,
        keywordsKey: 'nav.items.projectAdvances.keywords',
        descriptionKey: 'nav.items.projectAdvances.description',
      },
    ],
  },
  {
    id: 's-12.1',
    kind: 'catalogue',
    fsd: '12.1',
    labelKey: 'nav.fsd.s12_1.short',
    fullTitleKey: 'nav.fsd.s12_1.full',
    items: [
      {
        id: 'project-risks',
        labelKey: 'projects.c11.routeTitles.risks',
        to: '/projects/risks',
        icon: 'alert',
        permission: PROJECTS_PERMISSIONS.projectRead,
        keywordsKey: 'nav.items.projectRisks.keywords',
        descriptionKey: 'nav.items.projectRisks.description',
        code: 'PRJ-FR-017',
      },
      {
        id: 'project-subcontracts',
        labelKey: 'projects.c11.routeTitles.subcontracts',
        to: '/projects/subcontracts',
        icon: 'building',
        permission: PROJECTS_PERMISSIONS.projectRead,
        keywordsKey: 'nav.items.projectSubcontracts.keywords',
        descriptionKey: 'nav.items.projectSubcontracts.description',
        code: 'PRJ-FR-018',
      },
    ],
  },
]
